import { ConnectionQuality, Track, type Participant, type TrackPublication, type VideoTrack } from 'livekit-client';
import {
  ChevronUp,
  LayoutTemplate,
  Maximize,
  Maximize2,
  Mic,
  MicOff,
  Minimize,
  Minimize2,
  Monitor,
  MonitorOff,
  PhoneOff,
  Signal,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ComponentProps, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { firstName } from '../lib/avatar';
import { enterFullscreen, useFullscreen, useFullscreenSupported, useIdle } from '../lib/fullscreen';
import { canShareScreen, useHasKeyboard } from '../lib/media';
import { useDevices } from '../state/devices';
import { useVoice } from '../state/voice';
import { Avatar } from './Avatar';
import { ProfileButton } from './ProfileCard';
import { AudioMenu, VideoMenu, ViewMenu } from './CallMenus';
import { useDismiss } from '../lib/dismiss';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useChannels } from './Sidebar';

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
const MIC_SHORTCUT = isMac ? '⌥ M' : 'Alt + M';

export interface Person {
  identity: string;
  name: string;
  isLocal: boolean;
  micOn: boolean;
  camera: VideoTrack | null;
  screen: TrackPublication | null;
  quality: ConnectionQuality;
}

function visibleVideo(publication: TrackPublication | undefined): VideoTrack | null {
  if (!publication?.track || publication.isMuted) return null;
  return publication.track as VideoTrack;
}

/** A plain view of the LiveKit room, rebuilt whenever the voice store bumps its version. */
export function usePeople(): Person[] {
  const room = useVoice((s) => s.room);
  const version = useVoice((s) => s.version);
  return useMemo(() => {
    if (!room) return [];
    const all: Participant[] = [room.localParticipant, ...room.remoteParticipants.values()];
    return all.map((p) => ({
      identity: p.identity,
      name: p.name || p.identity,
      isLocal: p === room.localParticipant,
      micOn: p.isMicrophoneEnabled,
      camera: visibleVideo(p.getTrackPublication(Track.Source.Camera)),
      screen: p.getTrackPublication(Track.Source.ScreenShare) ?? null,
      quality: p.connectionQuality,
    }));
    // `version` is the change signal for the mutable LiveKit room.
  }, [room, version]);
}

/** The call inside a channel: tiles (or a watched screen share) and the call controls. `compact` (pop-out window) drops the shortcut hint. */
export function CallStage({ compact = false }: { compact?: boolean }) {
  const voice = useVoice();
  const people = usePeople();
  const hasKeyboard = useHasKeyboard();
  const view = useDevices((s) => s.shareView);
  const section = useRef<HTMLElement>(null);
  const canFullscreen = useFullscreenSupported(section);
  const sharers = people.filter((p) => p.screen);
  // Your own share can be watched too, straight from the local track. On screen as soon as it is
  // chosen: someone else's picture follows a moment later.
  const watched = sharers.find((p) => voice.watching.has(p.identity));
  // What a view chosen before watching anything starts with: someone else's share, or else yours.
  const first = sharers.find((p) => !p.isLocal) ?? sharers[0];

  const shareBox = (identity: string) => section.current?.querySelector(`[data-share="${CSS.escape(identity)}"]`);
  // Full screen asked for a share not on screen yet: entered once it is, still within the click's grace.
  const pendingFullscreen = useRef<string | null>(null);
  useEffect(() => {
    const identity = pendingFullscreen.current;
    if (!identity) return;
    const box = shareBox(identity);
    if (box) enterFullscreen(box);
    if (box || !sharers.some((p) => p.identity === identity)) pendingFullscreen.current = null;
  });

  const viewControl: ViewControl | null =
    sharers.length === 0
      ? null
      : {
          view,
          onView(next) {
            useDevices.getState().set({ shareView: next });
            if (!watched && first) voice.watch(first.identity);
          },
          onFullscreen: canFullscreen
            ? () => {
                const target = watched ?? first;
                if (!target) return;
                const box = shareBox(target.identity);
                if (box) return enterFullscreen(box);
                pendingFullscreen.current = target.identity;
                voice.watch(target.identity);
              }
            : null,
          shares: sharers.map((p) => ({ identity: p.identity, label: p.isLocal ? 'Senin ekranın' : firstName(p.name) })),
          watching: watched?.identity ?? null,
          // One at a time: the stage shows one share, and the others stop streaming to us.
          onWatch(identity) {
            for (const other of voice.watching) if (other !== identity) voice.unwatch(other);
            voice.watch(identity);
          },
        };

  return (
    <>
      <section
        ref={section}
        aria-label="Katılımcılar"
        className="@container min-h-0 flex-1 overflow-y-auto bg-[radial-gradient(ellipse_60%_55%_at_28%_30%,rgb(89_90_186/0.16)_0%,rgb(255_0_138/0.04)_45%,transparent_75%)] p-3 md:p-4"
      >
        {watched && view !== 'grid' ? (
          <Stage person={watched} people={people} speaking={voice.speaking} strip={view === 'stage'} onStop={() => voice.unwatch(watched.identity)} />
        ) : (
          <TileGrid count={people.length + sharers.length}>
            {people.map((person) => (
              <PersonTile key={person.identity} person={person} speaking={voice.speaking.has(person.identity)} />
            ))}
            {sharers.map((p) =>
              p.isLocal ? (
                <OwnShareTile
                  key="share-own"
                  person={p}
                  people={people}
                  speaking={voice.speaking}
                  // In the grid it is already as large as a watched share gets there.
                  onView={view === 'grid' ? null : () => voice.watch(p.identity)}
                  onStop={() => void voice.toggleScreen()}
                />
              ) : voice.watching.has(p.identity) ? (
                <ShareScreen
                  key={`share-${p.identity}`}
                  person={p}
                  people={people}
                  speaking={voice.speaking}
                  // Below its badge, as on your own share: a small tile has no room for both in one row.
                  actionsAt="bottom"
                  className="col-span-full aspect-video rounded-[18px] border border-line @xl:col-span-1"
                  actions={<StopWatching person={p} onStop={() => voice.unwatch(p.identity)} />}
                />
              ) : (
                <ShareTile key={`share-${p.identity}`} person={p} onWatch={() => voice.watch(p.identity)} />
              ),
            )}
          </TileGrid>
        )}
      </section>
      {!voice.micOn && hasKeyboard && !compact && (
        <div className="flex justify-center px-5 pb-3">
          <p className="m-0 flex flex-wrap items-center justify-center gap-2 rounded-full bg-surface-2 px-3.5 py-[7px] text-[13px] text-fg-2">
            <MicOff size={15} aria-hidden="true" />
            Mikrofonun kapalı. Konuşmak için
            <kbd className="rounded-xs border border-line-strong bg-surface-3 px-[7px] py-0.5 font-mono text-xs text-fg-1">{MIC_SHORTCUT}</kbd>
            tuşlarına bas.
          </p>
        </div>
      )}
      <Controls quality={people.find((p) => p.isLocal)?.quality ?? ConnectionQuality.Unknown} viewControl={viewControl} />
    </>
  );
}

/** What the "Görünüm" button needs; only while someone (you too) shares a screen. */
type ViewControl = Omit<ComponentProps<typeof ViewMenu>, 'onClose'>;

/** Messages from the voice store: kicked, moved to another device, connection trouble. */
export function Notice() {
  const text = useVoice((s) => s.notice);
  const rejoin = useVoice((s) => s.rejoin);
  if (!text) return null;
  return (
    <div role="status" className="mx-3 mt-3 flex animate-enter items-center gap-3 rounded-md border border-line-strong bg-surface-2 px-3.5 py-2.5 text-sm md:mx-5">
      <span className="flex-1">{text}</span>
      {rejoin && (
        <button type="button" onClick={() => void useVoice.getState().join(rejoin)} className="nc-press h-8 rounded-sm border-0 bg-brand-500 px-3 text-[13px] font-bold text-white">
          Buraya geri al
        </button>
      )}
      <button type="button" aria-label="Kapat" onClick={() => useVoice.getState().dismissNotice()} className="grid size-7 place-items-center rounded-xs border-0 bg-transparent text-fg-2 hover:bg-surface-3">
        <X size={15} />
      </button>
    </div>
  );
}

export function VideoView({ track, mirror = false, fit = 'cover' }: { track: VideoTrack; mirror?: boolean; fit?: 'cover' | 'contain' }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    track.attach(element);
    return () => {
      track.detach(element);
    };
  }, [track]);
  return (
    <video
      ref={ref}
      muted
      playsInline
      autoPlay
      className={`absolute inset-0 size-full ${fit === 'cover' ? 'object-cover' : 'object-contain'} ${mirror ? '-scale-x-100' : ''}`}
    />
  );
}

function NameTag({ person }: { person: Person }) {
  return (
    <ProfileButton
      user={{ id: person.identity, name: person.name }}
      className="absolute bottom-2 left-2 flex max-w-[calc(100%-16px)] items-center gap-1.5 truncate rounded-[9px] bg-black/55 py-[5px] pr-2.5 pl-2 text-[13px] font-semibold backdrop-blur-sm hover:bg-black/70 md:bottom-3 md:left-3"
    >
      {person.micOn ? <Mic size={14} className="text-brand-400" /> : <MicOff size={14} className="text-fg-3" />}
      {firstName(person.name)}
      {person.isLocal && <span className="font-medium text-fg-3">(sen)</span>}
      {(person.quality === ConnectionQuality.Poor || person.quality === ConnectionQuality.Lost) && (
        <Signal size={13} className="text-red-400" aria-label="Bağlantısı zayıf" />
      )}
    </ProfileButton>
  );
}

/**
 * Columns follow the number of tiles and the width of the pane itself (container queries), not
 * the viewport: a tablet's pane next to the sidebar is narrower than a phone in landscape.
 * (`auto-fit` was no good: it counts columns by their maximum width and collapses to one.)
 */
function TileGrid({ count, children }: { count: number; children: React.ReactNode }) {
  const narrow = count <= 1 ? 1 : 2;
  const medium = count <= 1 ? 1 : count <= 4 ? 2 : 3;
  const wide = count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
  const maxWidth = ['', '720px', '1100px', '1280px', 'none'][wide];
  return (
    <div
      className="mx-auto grid min-h-full w-full grid-cols-[repeat(var(--cols-n),minmax(0,1fr))] content-center gap-2.5 @xl:grid-cols-[repeat(var(--cols-m),minmax(0,1fr))] @xl:gap-3 @5xl:max-w-(--grid-max) @5xl:grid-cols-[repeat(var(--cols-w),minmax(0,1fr))]"
      style={{ ['--cols-n' as string]: narrow, ['--cols-m' as string]: medium, ['--cols-w' as string]: wide, ['--grid-max' as string]: maxWidth }}
    >
      {children}
    </div>
  );
}

function PersonTile({ person, speaking, compact = false }: { person: Person; speaking: boolean; compact?: boolean }) {
  return (
    <div
      className={`group relative grid place-items-center overflow-hidden rounded-[18px] [container-type:size] ${speaking ? 'nc-tile-live' : 'border border-line bg-surface-2'} ${compact ? 'h-24 w-32 flex-none md:h-32 md:w-48' : 'aspect-[4/3] @xl:aspect-video'}`}
    >
      {person.camera ? (
        <VideoView track={person.camera} mirror={person.isLocal} />
      ) : (
        // Lifted a little so the name tag never covers it in small tiles. The speaking ring may grow
        // as far as the tile's top edge: half the tile's height minus the lifted avatar's half.
        <Avatar id={person.identity} name={person.name} size="fluid" speaking={speaking} large className="-translate-y-[6%] [--nc-ring-max:calc(50cqh_-_0.56_*_var(--av))]" />
      )}
      <NameTag person={person} />
      {!person.isLocal && !compact && <VolumeControl identity={person.identity} name={firstName(person.name)} />}
    </div>
  );
}

/** Per-person volume; visible on hover with a mouse, always on touch screens. */
function VolumeControl({ identity, name }: { identity: string; name: string }) {
  const volume = useVoice((s) => s.volumes[identity] ?? 1);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  return (
    <div ref={box} className="absolute top-2 right-2 flex items-center gap-2 md:top-3 md:right-3">
      {open && (
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(volume * 100)}
          aria-label={`${name} ses seviyesi`}
          onChange={(e) => useVoice.getState().setVolume(identity, Number(e.target.value) / 100)}
          className="w-24 accent-brand-500"
        />
      )}
      <button
        type="button"
        aria-label={`${name} ses seviyesi`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`grid size-8 place-items-center rounded-sm border-0 bg-black/55 text-fg-1 backdrop-blur-sm [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 ${open || volume !== 1 ? '[@media(hover:hover)]:opacity-100' : ''}`}
      >
        {volume === 0 ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
    </div>
  );
}

function ShareTile({ person, onWatch }: { person: Person; onWatch: () => void }) {
  return (
    // Its own container: a small tile (six people) keeps the badge and the button, not the sentence.
    <div className="@container relative col-span-full flex aspect-video flex-col items-center justify-center gap-3.5 overflow-hidden rounded-[18px] border border-line bg-surface-2 p-5 pt-11 text-center @xl:col-span-1 @max-xs:p-3 @max-xs:pt-10">
      <span className="absolute top-3 left-3 flex animate-chip-pulse items-center gap-[5px] rounded-[7px] bg-brand-500 px-2 py-1 text-[11px] font-bold tracking-[0.06em] text-white">
        <Monitor size={12} />
        CANLI
      </span>
      <p className="m-0 text-sm text-fg-2 @max-xs:hidden">
        <span className="font-bold text-fg-1">{firstName(person.name)}</span> ekranını paylaşıyor
      </p>
      <button type="button" onClick={onWatch} className="nc-press nc-cta flex h-[42px] items-center gap-2 rounded-md border-0 bg-brand-500 px-[18px] text-sm font-bold text-white">
        <Monitor size={16} />
        Yayını izle
      </button>
    </div>
  );
}

/** What you share, as the others see it: from the local track, so nothing extra goes over the network. */
function OwnShareTile({
  person,
  people,
  speaking,
  onView,
  onStop,
}: {
  person: Person;
  people: Person[];
  speaking: ReadonlySet<string>;
  onView: (() => void) | null;
  onStop: () => void;
}) {
  return (
    <ShareScreen
      person={person}
      people={people}
      speaking={speaking}
      label="Paylaştığın ekran"
      actionsAt="bottom"
      className="col-span-full aspect-video rounded-[18px] border border-line @xl:col-span-1"
      actions={
        <>
          {/* In a small tile (many people, a narrow pane) only their icons fit. */}
          {person.screen?.track && onView && (
            <button type="button" onClick={onView} aria-label="Büyüt" className={overlayButton}>
              <Maximize2 size={15} aria-hidden="true" />
              <span className="@max-xs:hidden">Büyüt</span>
            </button>
          )}
          <button type="button" onClick={onStop} aria-label="Durdur" className={overlayButton}>
            <MonitorOff size={15} className="text-red-400" aria-hidden="true" />
            <span className="@max-xs:hidden">Durdur</span>
          </button>
        </>
      }
    />
  );
}

/** The watched share large; with `strip` ("Yayın ve kişiler") everyone in a row of small cards under it. */
function Stage({ person, people, speaking, strip, onStop }: { person: Person; people: Person[]; speaking: ReadonlySet<string>; strip: boolean; onStop: () => void }) {
  return (
    <div className="flex h-full flex-col gap-3">
      <ShareScreen
        person={person}
        people={people}
        speaking={speaking}
        // "Sadece yayın" hides the cards, and with them who is talking.
        showSpeakers={!strip}
        className="nc-grad-border min-h-0 flex-1 rounded-[18px]"
        style={{ ['--nc-fill' as string]: '#000' }}
        actions={<StopWatching person={person} onStop={onStop} />}
      />
      {strip && (
        <div className="flex gap-2 overflow-x-auto pb-1 md:gap-3">
          {people.map((p) => (
            <PersonTile key={p.identity} person={p} speaking={speaking.has(p.identity)} compact />
          ))}
        </div>
      )}
    </div>
  );
}

const overlayButton = 'nc-press flex h-9 items-center gap-1.5 rounded-sm border-0 bg-black/60 px-3 text-[13px] font-semibold text-fg-1 backdrop-blur-sm';

/** Back to the tiles; for your own share "Küçült". The words go when the picture is narrow. */
function StopWatching({ person, onStop }: { person: Person; onStop: () => void }) {
  const label = person.isLocal ? 'Küçült' : 'İzlemeyi bırak';
  return (
    <button type="button" onClick={onStop} aria-label={label} className={overlayButton}>
      {person.isLocal ? <Minimize2 size={15} aria-hidden="true" /> : <MonitorOff size={15} aria-hidden="true" />}
      <span className="@max-md:hidden">{label}</span>
    </button>
  );
}

function LiveChip({ person }: { person: Person }) {
  return (
    <span className="flex flex-none animate-chip-pulse items-center gap-[5px] rounded-[7px] bg-brand-500 px-2 py-1 text-[11px] font-bold tracking-[0.06em] text-white">
      <Monitor size={12} aria-hidden="true" />
      CANLI · {person.isLocal ? 'Senin ekranın' : firstName(person.name)}
    </span>
  );
}

/** Who is speaking right now, where the people's cards are not on screen; nothing while everyone is quiet. */
function Speakers({ people, speaking }: { people: Person[]; speaking: ReadonlySet<string> }) {
  const names = people.filter((p) => speaking.has(p.identity)).map((p) => (p.isLocal ? 'Sen' : firstName(p.name)));
  if (names.length === 0) return null;
  const shown = names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
  return (
    <span role="group" aria-label="Konuşanlar" className="flex min-w-0 items-center gap-1.5 rounded-[7px] bg-black/60 px-2 py-1 text-xs font-semibold text-fg-1 backdrop-blur-sm">
      <Mic size={12} className="flex-none text-green-400" aria-hidden="true" />
      <span className="truncate">{shown}</span>
    </span>
  );
}

/**
 * A screen share's picture with its buttons, wherever it is shown large: the stage, "Sadece yayın",
 * a watched tile in the grid, your own share. It goes full screen itself (also on a double-click),
 * so its overlays come along; there the buttons make way for `FullscreenControls`.
 */
function ShareScreen({
  person,
  people,
  speaking,
  showSpeakers = false,
  label,
  actions,
  actionsAt = 'top',
  className,
  style,
}: {
  person: Person;
  people: Person[];
  speaking: ReadonlySet<string>;
  showSpeakers?: boolean;
  label?: string;
  actions: ReactNode;
  actionsAt?: 'top' | 'bottom';
  className: string;
  style?: CSSProperties;
}) {
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const full = useFullscreen(box);
  const { idle, wake, hide, hold } = useIdle(full.active, panel);
  const hasKeyboard = useHasKeyboard();
  // How the last press came: a double-tap on a touch screen must not leave full screen.
  const lastPointer = useRef('mouse');
  const track = person.screen?.track as VideoTrack | undefined;
  const fullscreenButton = full.supported && (
    <button type="button" onClick={full.enter} aria-label="Tam ekran" title={hasKeyboard ? 'Tam ekran (Esc ile çıkarsın)' : 'Tam ekran'} className={overlayButton}>
      <Maximize size={15} aria-hidden="true" />
      <span className="@max-md:hidden">Tam ekran</span>
    </button>
  );
  return (
    <div
      ref={box}
      role="group"
      aria-label={label ?? `Yayın · ${person.isLocal ? 'Senin ekranın' : firstName(person.name)}`}
      data-share={person.identity}
      // With a mouse only: on a touch screen two quick taps show and hide the controls, nothing more.
      onDoubleClick={(event) => {
        if (lastPointer.current !== 'touch' && full.supported && !(event.target as Element).closest('button, input')) full.toggle();
      }}
      onPointerDown={(event) => {
        lastPointer.current = event.pointerType;
        if (event.pointerType !== 'touch') wake?.();
      }}
      onPointerMove={(event) => {
        if (event.pointerType !== 'touch') wake?.();
      }}
      // A touch screen has no mouse to move: a tap on the picture brings the controls back, the next one hides them.
      onPointerUp={(event) => {
        if (!full.active || event.pointerType !== 'touch') return;
        const target = event.target as Element;
        if (idle || target.closest('button, input') || panel.current?.contains(target)) wake?.();
        else hide();
      }}
      onKeyDown={wake}
      className={`@container relative overflow-hidden bg-black [&:fullscreen]:rounded-none [&:fullscreen]:border-0 ${idle ? 'cursor-none' : ''} ${className}`}
      style={style}
    >
      {track ? (
        <VideoView track={track} fit="contain" />
      ) : (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-fg-3">
          <Monitor size={28} aria-hidden="true" />
          {!person.isLocal && 'Yayın açılıyor…'}
        </span>
      )}
      {full.active ? (
        <FullscreenControls person={person} people={people} speaking={speaking} idle={idle} panel={panel} hold={hold} onExit={full.exit} />
      ) : (
        <>
          <div className="pointer-events-none absolute inset-x-3 top-3 flex items-start gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <LiveChip person={person} />
              {showSpeakers && <Speakers people={people} speaking={speaking} />}
            </div>
            {actionsAt === 'top' && (
              <div className="pointer-events-auto flex flex-none gap-2">
                {fullscreenButton}
                {actions}
              </div>
            )}
          </div>
          {actionsAt === 'bottom' && (
            <div className="absolute right-3 bottom-3 flex gap-2">
              {fullscreenButton}
              {actions}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Over a full-screen share: whose it is, who is speaking, the mic and the way out. They fade after 3 s without the mouse moving. */
function FullscreenControls({
  person,
  people,
  speaking,
  idle,
  panel,
  hold,
  onExit,
}: {
  person: Person;
  people: Person[];
  speaking: ReadonlySet<string>;
  idle: boolean;
  panel: RefObject<HTMLDivElement | null>;
  hold: { onPointerEnter: () => void; onPointerLeave: () => void };
  onExit: () => void;
}) {
  return (
    <div data-idle={idle} className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${idle ? 'opacity-0' : 'opacity-100'}`}>
      <div className="absolute inset-x-0 top-0 flex flex-wrap items-center gap-2 bg-linear-to-b from-black/70 to-transparent px-4 pt-4 pb-12 md:px-6">
        <LiveChip person={person} />
        <Speakers people={people} speaking={speaking} />
      </div>
      <div
        ref={panel}
        {...hold}
        className={`absolute bottom-[max(24px,env(safe-area-inset-bottom))] left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-white/10 bg-black/70 p-2 backdrop-blur-md ${idle ? '' : 'pointer-events-auto'}`}
      >
        <MicToggle className="nc-press grid h-12 w-12 place-items-center rounded-lg border-0" />
        <button type="button" onClick={onExit} className="nc-press flex h-12 items-center gap-2 rounded-lg border-0 bg-surface-2 px-4 text-sm font-semibold whitespace-nowrap text-fg-1 hover:bg-surface-3">
          <Minimize size={18} aria-hidden="true" />
          Tam ekrandan çık
        </button>
      </div>
    </div>
  );
}

/** In the same words and colours as the call bar: LiveKit's "good" is only fair (yellow). */
const qualityText: Record<ConnectionQuality, string> = {
  [ConnectionQuality.Excellent]: 'Bağlantı iyi',
  [ConnectionQuality.Good]: 'Bağlantı orta',
  [ConnectionQuality.Poor]: 'Bağlantı zayıf',
  [ConnectionQuality.Lost]: 'Bağlantı koptu',
  [ConnectionQuality.Unknown]: 'Bağlantı ölçülüyor',
};

/** Active controls are green; a closed mic is red so it is never mistaken for an open one. */
const tone = {
  neutral: 'bg-surface-2 text-fg-1 hover:bg-surface-3',
  on: 'bg-green-400/15 text-green-400 hover:bg-green-400/25',
  off: 'bg-red-400/15 text-red-400 hover:bg-red-400/25',
};

/** The mic on or off, as on the call bar; also over a full-screen share. */
function MicToggle({ className }: { className: string }) {
  const micOn = useVoice((s) => s.micOn);
  const mayTalk = useVoice((s) => s.may.mic);
  const label = micOn ? 'Mikrofonu kapat' : 'Mikrofonu aç';
  return (
    <button
      type="button"
      onClick={() => void useVoice.getState().toggleMic()}
      aria-pressed={micOn}
      aria-label={label}
      aria-disabled={!mayTalk && !micOn}
      title={!mayTalk && !micOn ? 'Bu kanalda konuşma iznin yok' : `${label} (${MIC_SHORTCUT})`}
      className={`${className} ${micOn ? tone.on : tone.off} aria-disabled:opacity-50`}
    >
      {micOn ? <Mic size={20} /> : <MicOff size={20} />}
    </button>
  );
}

function Controls({ quality, viewControl }: { quality: ConnectionQuality; viewControl: ViewControl | null }) {
  const voice = useVoice();
  const [menu, setMenu] = useState<'audio' | 'video' | 'view' | null>(null);
  const toggleMenu = (which: 'audio' | 'video' | 'view') => setMenu((current) => (current === which ? null : which));
  const button = 'nc-press grid h-12 place-items-center border-0 md:h-[52px]';
  const caret = `${button} w-7 border-l border-line-strong ${tone.neutral}`;

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 border-t border-line bg-surface-0 px-3 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] md:px-5">
      <div className={`flex flex-[1_1_140px] items-center gap-2 text-[13px] max-md:hidden ${voice.connection === 'reconnecting' || quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost ? 'text-red-400' : quality === ConnectionQuality.Good ? 'text-yellow-500' : 'text-fg-3'}`}>
        <Signal size={16} aria-hidden="true" />
        {voice.connection === 'reconnecting' ? 'Yeniden bağlanıyor…' : qualityText[quality]}
      </div>
      <div className="flex items-center justify-center gap-2 md:gap-2.5">
        <div className="relative">
          <div className="flex overflow-hidden rounded-lg border border-line-strong">
            <MicToggle className={`${button} w-12 md:w-[52px]`} />
            <button type="button" data-menu-trigger aria-label="Ses seçenekleri" aria-expanded={menu === 'audio'} onClick={() => toggleMenu('audio')} className={caret}>
              <ChevronUp size={16} className={menu === 'audio' ? '' : 'rotate-180'} />
            </button>
          </div>
          {menu === 'audio' && <AudioMenu onClose={() => setMenu(null)} />}
        </div>
        <div className="relative">
          <div className="flex overflow-hidden rounded-lg border border-line-strong">
            <button
              type="button"
              onClick={() => void voice.toggleCamera()}
              aria-label={voice.cameraOn ? 'Kamerayı kapat' : 'Kamerayı aç'}
              aria-pressed={voice.cameraOn}
              aria-disabled={!voice.may.camera && !voice.cameraOn}
              title={!voice.may.camera && !voice.cameraOn ? 'Bu kanalda kamera açma iznin yok' : undefined}
              className={`${button} w-12 md:w-[52px] ${voice.cameraOn ? tone.on : tone.neutral} aria-disabled:opacity-50`}
            >
              {voice.cameraOn ? <Video size={20} /> : <VideoOff size={20} />}
            </button>
            <button type="button" data-menu-trigger aria-label="Kamera seçenekleri" aria-expanded={menu === 'video'} onClick={() => toggleMenu('video')} className={caret}>
              <ChevronUp size={16} className={menu === 'video' ? '' : 'rotate-180'} />
            </button>
          </div>
          {menu === 'video' && <VideoMenu onClose={() => setMenu(null)} />}
        </div>
        {canShareScreen && (
          <button
            type="button"
            onClick={() => void voice.toggleScreen()}
            aria-label={voice.screenOn ? 'Paylaşımı durdur' : 'Ekranını paylaş'}
            aria-pressed={voice.screenOn}
            aria-disabled={!voice.may.screen && !voice.screenOn}
            title={!voice.may.screen && !voice.screenOn ? 'Bu kanalda ekran paylaşma iznin yok' : undefined}
            className={`${button} w-12 rounded-lg border border-line-strong md:w-[52px] ${voice.screenOn ? tone.on : tone.neutral} aria-disabled:opacity-50`}
          >
            {voice.screenOn ? <MonitorOff size={20} /> : <Monitor size={20} />}
          </button>
        )}
        {viewControl && (
          <div className="relative">
            <button
              type="button"
              data-menu-trigger
              aria-label="Görünüm"
              title="Görünüm"
              aria-haspopup="dialog"
              aria-expanded={menu === 'view'}
              onClick={() => toggleMenu('view')}
              className={`${button} w-12 rounded-lg border border-line-strong md:w-[52px] ${tone.neutral} aria-expanded:bg-surface-3`}
            >
              <LayoutTemplate size={20} />
            </button>
            {menu === 'view' && <ViewMenu {...viewControl} onClose={() => setMenu(null)} />}
          </div>
        )}
        <LeaveButton />
      </div>
      {/* Balances the connection label so the buttons stay centred; settings live in the sidebar and the mic menu. */}
      <div className="flex-[1_1_140px] max-md:hidden" aria-hidden="true" />
    </div>
  );
}

/**
 * Leaving; for someone whose roles allow it (voice_moderate), a small menu that can also end the
 * call for everyone ("Görüşmeyi sonlandır"; the server checks nobody above them is in it).
 */
function LeaveButton() {
  const channelId = useVoice((s) => s.channelId);
  const { data: channels } = useChannels();
  const canEnd = !!channels?.find((c) => c.id === channelId)?.permissions.includes('voice_moderate');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  const leave = () => void useVoice.getState().leave();
  async function end() {
    setOpen(false);
    if (!channelId) return;
    try {
      await client.api.endCall(channelId);
    } catch (e) {
      useVoice.setState({ notice: errorMessage(e) });
      return;
    }
    // Out at once, without waiting for LiveKit to drop us with everyone else.
    leave();
  }
  const item = 'flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-3 py-2.5 text-left text-[13.5px] hover:bg-surface-3';
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={canEnd ? () => setOpen((o) => !o) : leave}
        aria-label="Görüşmeden ayrıl"
        aria-haspopup={canEnd ? 'menu' : undefined}
        aria-expanded={canEnd ? open : undefined}
        className="nc-press grid h-12 w-16 place-items-center rounded-lg border-0 bg-red-400 text-white md:h-[52px] md:w-[68px]"
      >
        <PhoneOff size={20} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 bottom-full z-20 mb-2 w-60 animate-enter rounded-md border border-line-strong bg-surface-2 p-1 shadow-2xl">
          <button type="button" role="menuitem" onClick={leave} className={`${item} text-fg-1`}>
            Görüşmeden ayrıl
          </button>
          <button type="button" role="menuitem" onClick={() => void end()} className={`${item} font-semibold text-[#EE9191]`}>
            Görüşmeyi herkes için sonlandır
          </button>
        </div>
      )}
    </div>
  );
}
